// ===========================================
// Google Calendar Tab
// Admin oversight of Google Calendar integration
// ===========================================

import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Switch,
  Stack,
  Divider,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  useIntegrationSettings,
  useUpdateIntegrationSettings,
  useGoogleCalendarStats,
} from '../../../hooks/useIntegrations';
import type { UserRole } from '../../../types/integrations.types';

// ===========================================
// CONSTANTS
// ===========================================

const ALL_ROLES: { value: UserRole; label: string }[] = [
  { value: 'ADMIN', label: 'Admin' },
  { value: 'TEACHER', label: 'Teacher' },
  { value: 'PARENT', label: 'Parent' },
  { value: 'STUDENT', label: 'Student' },
];

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function GoogleCalendarTab() {
  const { data: settings, isLoading: settingsLoading } = useIntegrationSettings();
  const { data: stats, isLoading: statsLoading, refetch: refetchStats } = useGoogleCalendarStats();
  const updateSettings = useUpdateIntegrationSettings();

  const [pendingRoles, setPendingRoles] = useState<UserRole[] | null>(null);

  const gcalSettings = settings?.googleCalendar;
  const currentRoles = pendingRoles ?? gcalSettings?.allowedRoles ?? [];

  const handleToggleEnabled = async () => {
    if (!gcalSettings) return;
    await updateSettings.mutateAsync({
      googleCalendar: { enabled: !gcalSettings.enabled },
    });
  };

  const handleRoleToggle = (role: UserRole) => {
    const newRoles = currentRoles.includes(role)
      ? currentRoles.filter((r) => r !== role)
      : [...currentRoles, role];
    setPendingRoles(newRoles);
  };

  const handleSaveRoles = async () => {
    if (pendingRoles === null) return;
    await updateSettings.mutateAsync({
      googleCalendar: { allowedRoles: pendingRoles },
    });
    setPendingRoles(null);
  };

  const handleCancelRoles = () => {
    setPendingRoles(null);
  };

  if (settingsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!gcalSettings) {
    return <Alert severity="error">Failed to load Google Calendar settings.</Alert>;
  }

  return (
    <Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Google Calendar integration allows users to sync their lessons and events to their personal
        Google Calendar. As an admin, you can control which user roles have access to this feature.
      </Typography>

      {/* Settings Card */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          avatar={<CalendarIcon color="primary" />}
          title="Google Calendar Settings"
          subheader="Configure calendar sync for your school"
        />
        <CardContent>
          <Stack spacing={3}>
            {/* Enable/Disable Toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="subtitle2">Enable Google Calendar Integration</Typography>
                <Typography variant="body2" color="text.secondary">
                  Allow users to connect their Google Calendar accounts
                </Typography>
              </Box>
              <Switch
                checked={gcalSettings.enabled}
                onChange={handleToggleEnabled}
                disabled={updateSettings.isPending}
              />
            </Box>

            <Divider />

            {/* Allowed Roles */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Allowed Roles
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select which user roles can connect their Google Calendar
              </Typography>
              <FormGroup row>
                {ALL_ROLES.map((role) => (
                  <FormControlLabel
                    key={role.value}
                    control={
                      <Checkbox
                        checked={currentRoles.includes(role.value)}
                        onChange={() => handleRoleToggle(role.value)}
                        disabled={!gcalSettings.enabled || updateSettings.isPending}
                      />
                    }
                    label={role.label}
                  />
                ))}
              </FormGroup>
              {pendingRoles !== null && (
                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSaveRoles}
                    disabled={updateSettings.isPending}
                  >
                    {updateSettings.isPending ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button variant="text" size="small" onClick={handleCancelRoles}>
                    Cancel
                  </Button>
                </Stack>
              )}
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Stats Card */}
      <Card>
        <CardHeader
          title="Connection Status"
          subheader="Overview of calendar connections by role"
          action={
            <Button
              startIcon={statsLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={() => refetchStats()}
              disabled={statsLoading}
              size="small"
            >
              Refresh
            </Button>
          }
        />
        <CardContent>
          {statsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : stats ? (
            <Stack spacing={3}>
              {/* Connected by Role */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Users Connected by Role
                </Typography>
                <Stack spacing={2}>
                  {stats.connectedByRole.map((roleStats) => (
                    <Box key={roleStats.role}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">{roleStats.role}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {roleStats.connected} / {roleStats.total}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={roleStats.total > 0 ? (roleStats.connected / roleStats.total) * 100 : 0}
                      />
                    </Box>
                  ))}
                </Stack>
              </Box>

              <Divider />

              {/* Sync Activity */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Sync Activity (Last 7 Days)
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell align="right">Synced</TableCell>
                        <TableCell align="right">Errors</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stats.syncActivity.map((day) => (
                        <TableRow key={day.date}>
                          <TableCell>
                            {new Date(day.date).toLocaleDateString('en-AU', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                            })}
                          </TableCell>
                          <TableCell align="right">{day.synced}</TableCell>
                          <TableCell align="right">
                            {day.errors > 0 ? (
                              <Chip label={day.errors} size="small" color="error" />
                            ) : (
                              day.errors
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Recent Errors */}
              {stats.recentSyncErrors.length > 0 && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" gutterBottom color="error">
                      Recent Sync Errors
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Lesson</TableCell>
                            <TableCell>Error</TableCell>
                            <TableCell>Time</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {stats.recentSyncErrors.map((error) => (
                            <TableRow key={`${error.lessonId}-${error.occurredAt}`}>
                              <TableCell>{error.lessonName}</TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  color="error"
                                  sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}
                                >
                                  {error.errorMessage}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {new Date(error.occurredAt).toLocaleString()}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </>
              )}
            </Stack>
          ) : (
            <Typography color="text.secondary">No data available</Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
